/**
 * Centro-portal BFF client for the mpx grid-states endpoints.
 *
 * Mirrors the mpx HTTP contract (see
 * `src/apps/backend/mpx/services/v1/grid_states.go`). The centro-portal
 * BFF (under XTRM-1829) proxies these routes 1:1 under the `/gateway`
 * prefix, so the same shapes are used here.
 *
 * Used by the mpx-backed `useMpxAgGridState` hook (XTRM-1795), added in a
 * follow-up PR. Tests and Storybook can mock this module directly to
 * exercise consumers without a BFF in the loop.
 */

import { AxiosInstance, isAxiosError } from "axios";

const BFF_BASE_URL = "/gateway";

// ── Domain shapes (mirror mpx) ─────────────────────────────────────

export type GridStateScopeType =
  | "global"
  | "rfp"
  | "proposal"
  | "proposal_plan";

/**
 * Discriminator for the persisted payload's shape.
 *
 * - `ag_grid` — the faithful mirror of AG-Grid's `api.getState()`
 *   envelope. Read-only support is maintained for rows written before
 *   the meta-envelope existed.
 * - `ag_grid_customized` — the OWNED meta-envelope that composes `ag_grid`
 *   with sibling concerns AG-Grid does not natively model (row order
 *   today; future: saved views, group collapse, custom column labels).
 *   This is the kind the FE writes today.
 */
export type GridStateKind = "ag_grid" | "ag_grid_customized";

/**
 * Identifies which grid view a persisted state belongs to.
 *
 * - `global` — no `id`; user-wide preferences for a grid that is not
 *   tied to a specific entity.
 * - `rfp` / `proposal` / `proposal_plan` — `id` is the entity UUID.
 */
export type GridStateScope =
  | { type: "global" }
  | { type: "rfp"; id: string }
  | { type: "proposal"; id: string }
  | { type: "proposal_plan"; id: string };

/**
 * AG-Grid `api.getState()` envelope. Sub-states are kept as opaque
 * values so the FE stays forward-compatible with AG-Grid's per-field
 * versioning; the BE applies the same policy.
 *
 * `version` is the AG-Grid version string AG-Grid produced the state
 * with and is used by AG-Grid for auto-migration on apply. It is
 * required by mpx and must be preserved verbatim.
 */
export interface AgGridStatePayload {
  version: string;
  [subState: string]: unknown;
}

/**
 * Sibling field of {@link AgGridCustomizedPayload} recording the user's row
 * order as an ordered list of row IDs. Rows not present here are
 * appended after, in their natural order; ids that no longer exist are
 * ignored on apply. See `domain.RowOrder` in mpx.
 */
export interface RowOrderPayload {
  ids: string[];
}

/** Current schema version of the {@link AgGridCustomizedPayload} envelope. */
export const AG_GRID_VIEW_SCHEMA_VERSION = 1;

/**
 * The OWNED meta-envelope around an `AgGridStatePayload`. New sibling
 * fields are added optionally and additively; consumers ignore what
 * they do not understand. See `domain.AgGridCustomized` in mpx.
 */
export interface AgGridCustomizedPayload {
  /** Meta-envelope schema version. Distinct from `agGrid.version`. */
  version: number;
  agGrid: AgGridStatePayload;
  rowOrder?: RowOrderPayload;
}

/**
 * Discriminated union of persisted payload shapes the FE may receive on
 * GET. The FE writes only `ag_grid_customized`; `ag_grid` is read-only
 * compatibility for rows that predate the meta envelope.
 */
export type GridStatePayload =
  | { kind: "ag_grid"; payload: AgGridStatePayload }
  | { kind: "ag_grid_customized"; payload: AgGridCustomizedPayload };

export type GridStateResponse = GridStatePayload & {
  scopeType: GridStateScopeType;
  /** Omitted by the API for global scope. */
  scopeId?: string;
  gridId: string;
};

export type UpsertGridStateRequest = GridStatePayload;

// ── URL building ───────────────────────────────────────────────────

function scopePathSegment(scope: GridStateScope): string {
  switch (scope.type) {
    case "global":
      return "";
    case "rfp":
      return `/rfps/${encodeURIComponent(scope.id)}`;
    case "proposal":
      return `/proposals/${encodeURIComponent(scope.id)}`;
    case "proposal_plan":
      return `/proposal-plans/${encodeURIComponent(scope.id)}`;
  }
}

/**
 * Builds the BFF URL for a given scope + grid. Exported for tests and
 * for callers that need the URL for tracing/logging.
 */
export function buildGridStateUrl(
  scope: GridStateScope,
  gridId: string,
): string {
  return `${BFF_BASE_URL}${scopePathSegment(scope)}/grid-states/${encodeURIComponent(gridId)}`;
}

// ── Client ─────────────────────────────────────────────────────────

/**
 * GETs the persisted grid state for the current user + scope + gridId.
 *
 * Returns `null` when nothing is persisted (the BFF responds 404 in
 * that case). All other non-2xx responses propagate as the underlying
 * Axios error so callers can decide how to surface them.
 */
export async function getGridState(
  scope: GridStateScope,
  gridId: string,
  axiosInstance: AxiosInstance,
): Promise<GridStateResponse | null> {
  try {
    const response = await axiosInstance.get<GridStateResponse>(
      buildGridStateUrl(scope, gridId),
    );
    return response.data;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * PUTs the grid state for the current user + scope + gridId. The BFF
 * upserts (no separate create vs. update) and returns the stored
 * representation.
 */
export async function upsertGridState(
  scope: GridStateScope,
  gridId: string,
  body: UpsertGridStateRequest,
  axiosInstance: AxiosInstance,
): Promise<GridStateResponse> {
  const response = await axiosInstance.put<GridStateResponse>(
    buildGridStateUrl(scope, gridId),
    body,
  );
  return response.data;
}

/**
 * Builds an `ag_grid_customized` upsert request from an AG-Grid state and
 * optional row order. Centralized so call sites do not duplicate the
 * meta-envelope shape (and so a future `version` bump is one edit).
 */
export function buildAgGridCustomizedRequest(
  agGrid: AgGridStatePayload,
  rowOrder?: RowOrderPayload,
): UpsertGridStateRequest {
  const payload: AgGridCustomizedPayload = {
    version: AG_GRID_VIEW_SCHEMA_VERSION,
    agGrid,
  };

  if (rowOrder) {
    payload.rowOrder = rowOrder;
  }

  return { kind: "ag_grid_customized", payload };
}

/**
 * Extracts the AG-Grid state from either kind. `ag_grid` rows surface
 * their payload directly; `ag_grid_customized` rows return `payload.agGrid`.
 */
export function extractAgGridState(
  response: GridStateResponse | null | undefined,
): AgGridStatePayload | undefined {
  if (!response) {
    return undefined;
  }

  if (response.kind === "ag_grid_customized") {
    return response.payload.agGrid;
  }

  return response.payload;
}

/**
 * Extracts the persisted row order from a response. Only `ag_grid_customized`
 * rows can carry one; `ag_grid` rows always return `undefined`.
 */
export function extractRowOrder(
  response: GridStateResponse | null | undefined,
): RowOrderPayload | undefined {
  if (response?.kind === "ag_grid_customized") {
    return response.payload.rowOrder;
  }

  return undefined;
}

/**
 * DELETEs the persisted grid state for the current user + scope +
 * gridId. Idempotent: the BFF treats a missing record as success.
 */
export async function deleteGridState(
  scope: GridStateScope,
  gridId: string,
  axiosInstance: AxiosInstance,
): Promise<void> {
  await axiosInstance.delete(buildGridStateUrl(scope, gridId));
}
